/**
 * Редактор меток интерактивной гиперболической карты (режим рисования).
 *
 * Позволяет администратору в режиме рисования выбрать элемент легенды и
 * расставлять его метки прямо на карте (Требование 9.1), сохраняя координаты
 * метки в **координатах поверхности** (в канонической системе фундаментальной
 * области, Требование 9.2), перемещать и удалять метки (Требование 9.3), с
 * немедленной перерисовкой иконок (Требование 9.4).
 *
 * Архитектура «тонкий DOM, чистое ядро», как и у {@link module:drawing-tools}:
 * - **Чистые мутации** ({@link MarkerEditor.selectLegendItem},
 *   {@link MarkerEditor.placeMarker}, {@link MarkerEditor.moveMarker},
 *   {@link MarkerEditor.removeMarker}, {@link MarkerEditor.setMarkers},
 *   {@link MarkerEditor.getMarkers}) не зависят от DOM/WebGL и покрываются
 *   юнит-тестами. Метка хранит координаты поверхности `z` и ссылку на элемент
 *   легенды `legendItemId`.
 * - **DOM-склейка** ({@link MarkerEditor.attach}) — тонкая обёртка над указателем:
 *   клик по пустому месту размещает метку в соответствующей точке поверхности
 *   (перевод пикселей в координаты поверхности через {@link DrawingLayer.screenToSurface},
 *   `V⁻¹`); клик по существующей метке выбирает её для перемещения/удаления;
 *   перетаскивание перемещает метку. DOM-часть безопасно вырождается в no-op вне
 *   браузера.
 *
 * После каждой мутации вызываются колбэки `onChange` (страница переустанавливает
 * метки в {@link MarkerLayer} и перерисовывает карту — немедленная отрисовка,
 * Требование 9.4) и `onMarkersChanged` (страница сохраняет метки через
 * `PUT /markers`, задача 20.2). Мутации всегда работают с координатами
 * поверхности, поэтому метки корректно отображаются при любом виде и уровне
 * детализации.
 *
 * Требования: 9.1, 9.2, 9.3, 9.4
 */

import type { Complex, MapMarker } from '@langton/shared';
import type { DrawingLayer } from './drawing-layer.js';
import type { MapViewController, ScreenPoint, Viewport } from './map-view-controller.js';
import type { MarkerLayer } from './marker-layer.js';

/**
 * Сгенерировать клиентский идентификатор метки. Использует `crypto.randomUUID`,
 * если он доступен, иначе — запасной генератор на основе времени и случайного
 * суффикса (для окружений без Web Crypto).
 */
export function generateMarkerId(): string {
  const cryptoObj = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  return `marker-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Проверить, что точка — корректное комплексное число (координаты поверхности). */
function isFiniteComplex(p: Complex | undefined | null): p is Complex {
  return (
    !!p &&
    typeof p.re === 'number' &&
    typeof p.im === 'number' &&
    Number.isFinite(p.re) &&
    Number.isFinite(p.im)
  );
}

/** Клонировать комплексную точку. */
function cloneComplex(p: Complex): Complex {
  return { re: p.re, im: p.im };
}

/** Клонировать метку (глубокая копия координат поверхности). */
function cloneMarker(m: MapMarker): MapMarker {
  return {
    id: m.id,
    mapId: m.mapId,
    legendItemId: m.legendItemId,
    surface: cloneComplex(m.surface),
  };
}

/** Порог в пикселях, отличающий клик от перетаскивания. */
const DRAG_THRESHOLD_PX = 4;

/** Опции конфигурации {@link MarkerEditor}. */
export interface MarkerEditorOptions {
  /** Идентификатор карты, к которой принадлежат метки. */
  mapId: string;
  /**
   * Слой рисования — используется как единый источник истины для перевода
   * координат экран→поверхность ({@link DrawingLayer.screenToSurface}).
   */
  layer?: DrawingLayer;
  /** Контроллер вида — предоставляет текущий вид `V` и масштаб для перевода координат. */
  controller?: MapViewController;
  /**
   * Слой меток — для hit-test существующих меток при клике/перетаскивании
   * ({@link MarkerLayer.hitTest}).
   */
  markerLayer?: MarkerLayer;
  /** Начальный список меток (координаты поверхности). */
  initialMarkers?: MapMarker[];
  /** Начально выбранный элемент легенды для размещения (Требование 9.1). */
  selectedLegendItemId?: string | null;
  /**
   * Колбэк изменения списка меток: вызывается после каждой мутации. Страница
   * переустанавливает метки в {@link MarkerLayer} и перерисовывает карту —
   * немедленная отрисовка (Требование 9.4).
   */
  onChange?: (markers: MapMarker[]) => void;
  /**
   * Колбэк для сохранения меток: вызывается после каждой мутации. Страница
   * сохраняет метки через `PUT /markers` (задача 20.2).
   */
  onMarkersChanged?: (markers: MapMarker[]) => void;
  /** Переопределение генератора идентификаторов (для тестов). */
  generateId?: () => string;
}

/** Активное взаимодействие указателя. */
type Interaction =
  | { kind: 'move'; id: string }
  | { kind: 'place' };

/**
 * Редактор меток карты: чистые мутации списка меток + тонкая DOM-склейка
 * размещения/перемещения/удаления в режиме рисования.
 *
 * Жизненный цикл:
 * 1. `new MarkerEditor({ mapId, layer, controller, markerLayer, onChange })`.
 * 2. `setMarkers(loaded)` — инициализация ранее сохранёнными метками.
 * 3. `selectLegendItem(id)` — выбор элемента легенды для размещения (Требование 9.1).
 * 4. `attach(canvas)` — обработчики указателя: клик размещает/выбирает,
 *    перетаскивание перемещает. Либо программные вызовы `placeMarker`/
 *    `moveMarker`/`removeMarker`.
 * 5. `detach()` / `dispose()` — снятие обработчиков.
 */
export class MarkerEditor {
  private readonly options: MarkerEditorOptions;
  private readonly mapId: string;
  private readonly makeId: () => string;

  /** Рабочий список меток (координаты поверхности). */
  private markers: MapMarker[] = [];
  /** Выбранный элемент легенды для размещения новых меток (Требование 9.1). */
  private selectedLegendItemId: string | null = null;
  /** Идентификатор выбранной метки (для перемещения/удаления, Требование 9.3). */
  private selectedMarkerId: string | null = null;

  // Состояние DOM-взаимодействия.
  private eventTarget: HTMLElement | null = null;
  private interaction: Interaction | null = null;
  private startPixel: ScreenPoint | null = null;
  private moved = false;
  private readonly pointerDownHandler = (e: PointerEvent): void => this.onPointerDown(e);
  private readonly pointerMoveHandler = (e: PointerEvent): void => this.onPointerMove(e);
  private readonly pointerUpHandler = (e: PointerEvent): void => this.onPointerUp(e);

  constructor(options: MarkerEditorOptions) {
    this.options = options;
    this.mapId = options.mapId;
    this.makeId = options.generateId ?? generateMarkerId;
    this.selectedLegendItemId = options.selectedLegendItemId ?? null;
    if (options.initialMarkers) {
      this.markers = options.initialMarkers.map(cloneMarker);
    }
  }

  // -------------------------------------------------------------------------
  // Чистые мутации списка меток (тестируемые без DOM)
  // -------------------------------------------------------------------------

  /**
   * Выбрать элемент легенды, из которого создаются новые метки (Требование 9.1).
   * Передайте `null`, чтобы сбросить выбор (размещение будет запрещено).
   *
   * @param legendItemId идентификатор элемента легенды либо `null`
   */
  selectLegendItem(legendItemId: string | null): void {
    this.selectedLegendItemId = legendItemId;
  }

  /** Идентификатор выбранного элемента легенды для размещения либо `null`. */
  getSelectedLegendItem(): string | null {
    return this.selectedLegendItemId;
  }

  /**
   * Разместить новую метку в точке поверхности (Требование 9.2). Метка получает
   * выбранный `legendItemId`, координаты поверхности и сгенерированный клиентский
   * идентификатор, добавляется в список и возвращается.
   *
   * Возвращает `null`, если элемент легенды не выбран (Требование 9.1) либо точка
   * поверхности некорректна.
   *
   * @param surface координаты метки на поверхности
   * @returns созданная метка либо `null`
   */
  placeMarker(surface: Complex): MapMarker | null {
    if (!this.selectedLegendItemId || !isFiniteComplex(surface)) {
      return null;
    }
    const marker: MapMarker = {
      id: this.makeId(),
      mapId: this.mapId,
      legendItemId: this.selectedLegendItemId,
      surface: cloneComplex(surface),
    };
    this.markers.push(marker);
    this.selectedMarkerId = marker.id;
    this.notify();
    return cloneMarker(marker);
  }

  /**
   * Переместить метку в новую точку поверхности (Требование 9.3): обновляет
   * координаты поверхности метки с заданным `id`.
   *
   * @param id идентификатор метки
   * @param surface новые координаты поверхности
   * @returns обновлённая метка либо `null`, если метка не найдена/точка некорректна
   */
  moveMarker(id: string, surface: Complex): MapMarker | null {
    if (!isFiniteComplex(surface)) {
      return null;
    }
    const marker = this.markers.find((m) => m.id === id);
    if (!marker) {
      return null;
    }
    marker.surface = cloneComplex(surface);
    this.notify();
    return cloneMarker(marker);
  }

  /**
   * Удалить метку по идентификатору (Требование 9.3).
   *
   * @param id идентификатор метки
   * @returns `true`, если метка существовала и была удалена
   */
  removeMarker(id: string): boolean {
    const index = this.markers.findIndex((m) => m.id === id);
    if (index < 0) {
      return false;
    }
    this.markers.splice(index, 1);
    if (this.selectedMarkerId === id) {
      this.selectedMarkerId = null;
    }
    this.notify();
    return true;
  }

  /** Копии всех меток в порядке добавления (координаты поверхности). */
  getMarkers(): MapMarker[] {
    return this.markers.map(cloneMarker);
  }

  /**
   * Инициализировать список меток ранее сохранёнными данными (координаты
   * поверхности). Не вызывает колбэки — это загрузка, а не пользовательская
   * мутация; сбрасывает выбранную метку.
   *
   * @param markers загруженные метки
   */
  setMarkers(markers: MapMarker[]): void {
    this.markers = (markers ?? []).map(cloneMarker);
    this.selectedMarkerId = null;
  }

  /** Идентификатор выбранной (для перемещения/удаления) метки либо `null`. */
  getSelectedMarkerId(): string | null {
    return this.selectedMarkerId;
  }

  /**
   * Выбрать метку для перемещения/удаления (Требование 9.3). Передайте `null`,
   * чтобы снять выбор. Не влияет на список меток и не вызывает колбэки.
   *
   * @param id идентификатор метки либо `null`
   */
  selectMarker(id: string | null): void {
    if (id === null || this.markers.some((m) => m.id === id)) {
      this.selectedMarkerId = id;
    }
  }

  /**
   * Удалить выбранную метку (Требование 9.3). Удобно для кнопки/горячей клавиши
   * удаления в UI.
   *
   * @returns `true`, если выбранная метка существовала и была удалена
   */
  removeSelectedMarker(): boolean {
    if (!this.selectedMarkerId) {
      return false;
    }
    return this.removeMarker(this.selectedMarkerId);
  }

  /** Вызвать колбэки изменения списка меток (немедленная отрисовка + сохранение). */
  private notify(): void {
    const snapshot = this.getMarkers();
    this.options.onChange?.(snapshot);
    this.options.onMarkersChanged?.(snapshot);
  }

  // -------------------------------------------------------------------------
  // DOM-склейка (тонкая) режима рисования
  // -------------------------------------------------------------------------

  /**
   * Перевести пиксельные координаты холста в координаты поверхности `V⁻¹(w)`,
   * делегируя {@link DrawingLayer.screenToSurface} (Требование 9.2). Требует
   * заданных `layer` и `controller`; иначе возвращает `null`.
   *
   * @param px координата X в пикселях
   * @param py координата Y в пикселях
   * @param viewport размеры холста в пикселях
   */
  screenToSurface(px: number, py: number, viewport: Viewport): Complex | null {
    const { layer, controller } = this.options;
    if (!layer || !controller) {
      return null;
    }
    return layer.screenToSurface(
      px,
      py,
      viewport,
      controller.getView(),
      controller.getScale(),
    );
  }

  /**
   * Подключить обработчики указателя к холсту (режим рисования): клик по пустому
   * месту размещает метку выбранного элемента легенды, клик по существующей метке
   * выбирает её, перетаскивание перемещает выбранную метку. Вся математика — в
   * чистых мутациях. Безопасно игнорируется вне браузера.
   *
   * @param eventTarget холст карты (источник событий указателя)
   */
  attach(eventTarget: HTMLElement): void {
    if (typeof window === 'undefined' || !eventTarget) {
      return;
    }
    this.detach();
    this.eventTarget = eventTarget;
    eventTarget.addEventListener('pointerdown', this.pointerDownHandler);
    eventTarget.addEventListener('pointermove', this.pointerMoveHandler);
    window.addEventListener('pointerup', this.pointerUpHandler);
  }

  /** Снять обработчики указателя и сбросить состояние взаимодействия. */
  detach(): void {
    if (this.eventTarget) {
      this.eventTarget.removeEventListener('pointerdown', this.pointerDownHandler);
      this.eventTarget.removeEventListener('pointermove', this.pointerMoveHandler);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('pointerup', this.pointerUpHandler);
    }
    this.eventTarget = null;
    this.interaction = null;
    this.startPixel = null;
    this.moved = false;
  }

  /** Снять обработчики и очистить рабочее состояние. */
  dispose(): void {
    this.detach();
    this.markers = [];
    this.selectedMarkerId = null;
  }

  /** Размеры текущего холста в пикселях (для перевода координат). */
  private viewportOf(target: HTMLElement): Viewport {
    const canvas = target as HTMLCanvasElement;
    return {
      width: canvas.width || target.clientWidth || 1,
      height: canvas.height || target.clientHeight || 1,
    };
  }

  /** Пиксельные координаты события указателя относительно холста. */
  private eventPixel(target: HTMLElement, e: MouseEvent): ScreenPoint {
    if (typeof target.getBoundingClientRect === 'function') {
      const rect = target.getBoundingClientRect();
      const canvas = target as HTMLCanvasElement;
      const scaleX = (canvas.width || rect.width) / (rect.width || 1);
      const scaleY = (canvas.height || rect.height) / (rect.height || 1);
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    }
    return { x: e.clientX, y: e.clientY };
  }

  /**
   * Нажатие указателя: клик по существующей метке выбирает её для возможного
   * перетаскивания; клик по пустому месту готовит размещение новой метки.
   */
  private onPointerDown(e: PointerEvent): void {
    if (!this.eventTarget) {
      return;
    }
    const pixel = this.eventPixel(this.eventTarget, e);
    this.startPixel = pixel;
    this.moved = false;

    const hit = this.options.markerLayer?.hitTest(pixel.x, pixel.y) ?? null;
    if (hit) {
      this.selectMarker(hit.marker.id);
      this.interaction = { kind: 'move', id: hit.marker.id };
    } else {
      this.interaction = { kind: 'place' };
    }
    this.capturePointer(e.pointerId);
  }

  /** Движение указателя: перетаскивание выбранной метки к курсору (Требование 9.3). */
  private onPointerMove(e: PointerEvent): void {
    if (!this.interaction || !this.eventTarget) {
      return;
    }
    const pixel = this.eventPixel(this.eventTarget, e);
    if (this.startPixel) {
      const dx = pixel.x - this.startPixel.x;
      const dy = pixel.y - this.startPixel.y;
      if (Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
        this.moved = true;
      }
    }
    if (this.interaction.kind === 'move' && this.moved) {
      const viewport = this.viewportOf(this.eventTarget);
      const surface = this.screenToSurface(pixel.x, pixel.y, viewport);
      if (surface) {
        this.moveMarker(this.interaction.id, surface);
      }
    }
  }

  /**
   * Отпускание указателя: если это был клик (без перетаскивания) по пустому
   * месту — разместить новую метку (Требования 9.2, 9.4).
   */
  private onPointerUp(e: PointerEvent): void {
    if (!this.interaction || !this.eventTarget) {
      this.resetInteraction(e.pointerId);
      return;
    }
    if (this.interaction.kind === 'place' && !this.moved) {
      const pixel = this.eventPixel(this.eventTarget, e);
      const viewport = this.viewportOf(this.eventTarget);
      const surface = this.screenToSurface(pixel.x, pixel.y, viewport);
      if (surface) {
        this.placeMarker(surface);
      }
    }
    this.resetInteraction(e.pointerId);
  }

  /** Сбросить состояние взаимодействия и освободить захват указателя. */
  private resetInteraction(pointerId: number): void {
    this.interaction = null;
    this.startPixel = null;
    this.moved = false;
    this.releasePointer(pointerId);
  }

  /** Захватить указатель холстом (если поддерживается). */
  private capturePointer(pointerId: number): void {
    const target = this.eventTarget as HTMLElement & {
      setPointerCapture?: (id: number) => void;
    };
    if (target && typeof target.setPointerCapture === 'function') {
      try {
        target.setPointerCapture(pointerId);
      } catch {
        // Некоторые окружения не поддерживают захват указателя — не критично.
      }
    }
  }

  /** Освободить захват указателя (если поддерживается). */
  private releasePointer(pointerId: number): void {
    const target = this.eventTarget as HTMLElement & {
      releasePointerCapture?: (id: number) => void;
    };
    if (target && typeof target.releasePointerCapture === 'function') {
      try {
        target.releasePointerCapture(pointerId);
      } catch {
        // Игнорируем: указатель мог не быть захвачен.
      }
    }
  }
}
